import { ValidationError } from '../../../shared/exceptions/validation.error';
import { BaseService } from '../../base';
import type { Coverage } from '../../coverages/types';
import type {
  AttachBusinessQuotePayload,
  BusinessUnderwritingMessage,
  BusinessUnderwritingMessagesResponse,
  CalculateAnalysisPayload,
  CalculateQuotePayload,
  CalculateQuoteResponse,
  CopyInsuredObjectResponse,
  CreateBusinessQuotePayload,
  CreateQuotePayload,
  CreateQuoteResponse,
  GetQuoteDocumentResponse,
  QuoteDocumentType,
  RejectQuoteInAnalysisResponse,
} from '../types';

const PORTAL_API_URL = import.meta.env.VITE_API_URL as string | undefined;

export class BusinessQuotesService extends BaseService {
  constructor() {
    super();
  }

  async createQuote(payload: CreateBusinessQuotePayload): Promise<{
    success: boolean;
    data?: CreateQuoteResponse;
    error?: string;
    message?: string;
  }> {
    try {
      const path = '/portal/v1/business/quotes';
      const { previousInsurer, sinister, sinisterValue, ...rest } = payload;
      const response = await this.post(
        path,
        {
          ...rest,
          validationEventCode: 'CLIENT_INFO',
          ...(previousInsurer ? { seguradoraAnterior: previousInsurer } : {}),
          ...(sinister ? { sinister } : {}),
          ...(sinisterValue ? { sinisterValue } : {}),
        },
        PORTAL_API_URL
      );

      if (response.status === 422) {
        const data = (await response.json()) as {
          detail?: string;
          errors: Record<string, { code: string; message: string }>;
        };

        const normalizedFieldErrors = Object.fromEntries(
          Object.entries(data.errors ?? {}).map(([key, value]) => [
            key,
            {
              ...value,
              message: String(value?.message ?? '').trim(),
            },
          ])
        ) as Record<string, { code: string; message: string }>;

        const messages =
          Object.values(normalizedFieldErrors)
            ?.map(error => error.message)
            .filter(Boolean)
            .map(message => message.trim()) ?? [];

        const validationError = new ValidationError(messages);
        const rawDetail = String(data.detail ?? '').trim();
        const detailFromResponse =
          rawDetail &&
          rawDetail !== 'Validation error' &&
          rawDetail !== 'A proposta foi rejeitada pela validação'
            ? rawDetail
            : undefined;

        const quoteStatusCode = String(
          normalizedFieldErrors.quoteStatus?.code ?? ''
        ).trim();
        const isQuoteClosed = quoteStatusCode === '2';

        if (isQuoteClosed) {
          const rejectMessage =
            normalizedFieldErrors.proposalReject?.message?.trim() ||
            normalizedFieldErrors.quoteStatus?.message?.trim() ||
            '';
          validationError.detail = rejectMessage || 'Cotação Encerrada';
          validationError.messages = [];
        } else if (detailFromResponse) {
          validationError.detail = detailFromResponse;
        } else if (
          messages.length > 0 &&
          messages.every(m => m.includes(' - '))
        ) {
          const prefixes = messages.map(m => m.split(' - ')[0].trim());
          if (prefixes.every(p => p === prefixes[0])) {
            validationError.detail = prefixes[0];
            validationError.messages = messages.map(m =>
              m.split(' - ').slice(1).join(' - ').trim()
            );
          }
        } else if (rawDetail && messages.length > 0) {
          validationError.detail = rawDetail;
        }

        validationError.fieldErrors = normalizedFieldErrors;
        throw validationError;
      }

      return await this.handleJsonResponse<CreateQuoteResponse>(response);
    } catch (error) {
      if (error instanceof ValidationError) throw error;

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao criar cotação',
      };
    }
  }

  async updateQuote(
    id: string,
    payload: Partial<AttachBusinessQuotePayload>
  ): Promise<{
    success: boolean;
    data?: CreateQuotePayload;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `/portal/v1/business/quotes/${id}/attach-element`;
      const { previousInsurer, ...rest } = payload;
      const response = await this.post(
        path,
        {
          ...rest,
          ...(previousInsurer ? { seguradoraAnterior: previousInsurer } : {}),
        },
        PORTAL_API_URL
      );

      if (response.status === 422) {
        const data = (await response.json()) as {
          detail?: string;
          errors: Record<string, { code: string; message: string }>;
        };

        const normalizedFieldErrors = Object.fromEntries(
          Object.entries(data.errors ?? {}).map(([key, value]) => [
            key,
            {
              ...value,
              message: String(value?.message ?? '').trim(),
            },
          ])
        ) as Record<string, { code: string; message: string }>;

        const messages =
          Object.values(normalizedFieldErrors)
            ?.map(error => error.message)
            .filter(Boolean)
            .map(message => message.trim()) ?? [];

        const validationError = new ValidationError(messages);
        const rawDetail = String(data.detail ?? '').trim();
        const detailFromResponse =
          rawDetail &&
          rawDetail !== 'Validation error' &&
          rawDetail !== 'A proposta foi rejeitada pela validação'
            ? rawDetail
            : undefined;

        const quoteStatusCode = String(
          normalizedFieldErrors.quoteStatus?.code ?? ''
        ).trim();
        const isQuoteClosed = quoteStatusCode === '2';

        if (isQuoteClosed) {
          const rejectMessage =
            normalizedFieldErrors.proposalReject?.message?.trim() ||
            normalizedFieldErrors.quoteStatus?.message?.trim() ||
            '';
          validationError.detail = rejectMessage || 'Cotação Encerrada';
          validationError.messages = [];
        } else if (detailFromResponse) {
          validationError.detail = detailFromResponse;
        } else if (
          messages.length > 0 &&
          messages.every(m => m.includes(' - '))
        ) {
          const prefixes = messages.map(m => m.split(' - ')[0].trim());
          if (prefixes.every(p => p === prefixes[0])) {
            validationError.detail = prefixes[0];
            validationError.messages = messages.map(m =>
              m.split(' - ').slice(1).join(' - ').trim()
            );
          }
        } else if (rawDetail && messages.length > 0) {
          validationError.detail = rawDetail;
        }

        validationError.fieldErrors = normalizedFieldErrors;
        throw validationError;
      }

      return await this.handleJsonResponse<CreateQuotePayload>(response);
    } catch (error) {
      if (error instanceof ValidationError) throw error;

      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao atualizar cotação',
      };
    }
  }
  async calculateQuote(payload: CalculateQuotePayload): Promise<{
    success: boolean;
    data?: CalculateQuoteResponse;
    error?: string;
    message?: string;
    underwritingMessages?: BusinessUnderwritingMessage[];
  }> {
    try {
      const path = `/portal/v1/business/quotes/${payload.policyId}/calculate`;
      const response = await this.post(path, {}, {}, PORTAL_API_URL);

      // Backend devolve 400 (ProblemDetails) + underwritingMessages quando a
      // subscrição automática bloqueia o cálculo. Nesse caso a cotação já foi
      // marcada como "Em Análise" no Insuremo (StatusPortal 17) — propagamos
      // as mensagens para o caller.
      if (response.status === 400) {
        const raw = await response.text();
        const body = raw
          ? ((): {
              detail?: string;
              message?: string;
              underwritingMessages?: BusinessUnderwritingMessage[];
            } | null => {
              try {
                return JSON.parse(raw);
              } catch {
                return null;
              }
            })()
          : null;

        // Presença do campo (mesmo vazio) sinaliza que a cotação foi enviada
        // para análise pelo backend; o caller deve seguir pro step de cálculo
        // mesmo sem mensagens no payload (lista completa vem pelo GET).
        if (body && Array.isArray(body.underwritingMessages)) {
          return {
            success: false,
            error:
              body.detail ||
              body.message ||
              'Cotação enviada para análise da subscrição',
            underwritingMessages: body.underwritingMessages,
          };
        }

        return {
          success: false,
          error:
            body?.detail ||
            body?.message ||
            `Erro ${response.status}: ${response.statusText}`,
        };
      }

      return await this.handleJsonResponse<CalculateQuoteResponse>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao calcular cotação',
      };
    }
  }

  async calculateAnalysis(
    policyId: string,
    payload: CalculateAnalysisPayload
  ): Promise<{
    success: boolean;
    data?: CalculateQuoteResponse;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `/portal/v1/business/quotes/${encodeURIComponent(policyId)}/calculate-analysis`;
      const response = await this.post(path, payload, PORTAL_API_URL);
      return await this.handleJsonResponse<CalculateQuoteResponse>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao calcular cotação em análise',
      };
    }
  }

  async rejectAnalysis(
    policyId: string,
    description: string
  ): Promise<{
    success: boolean;
    data?: RejectQuoteInAnalysisResponse;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `/portal/v1/business/quotes/${encodeURIComponent(policyId)}/reject-analysis`;
      const response = await this.post(
        path,
        { description },
        PORTAL_API_URL
      );
      return await this.handleJsonResponse<RejectQuoteInAnalysisResponse>(
        response
      );
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao recusar cotação em análise',
      };
    }
  }

  async getDocument(
    policyId: string,
    type: QuoteDocumentType = 'quote'
  ): Promise<{
    success: boolean;
    data?: GetQuoteDocumentResponse;
    error?: string;
  }> {
    try {
      const path = `/portal/v1/business/quotes/${encodeURIComponent(
        policyId
      )}/documents?type=${encodeURIComponent(type)}`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<GetQuoteDocumentResponse>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : type === 'refusal_letter'
              ? 'Erro ao carregar carta de recusa'
              : 'Erro ao carregar documento da cotação',
      };
    }
  }

  async submitAnalysisCalculation(policyId: string): Promise<{
    success: boolean;
    data?: unknown;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `/portal/v1/business/quotes/${encodeURIComponent(policyId)}/submit-analysis-calculation`;
      const response = await this.post(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<unknown>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao enviar cálculo da análise',
      };
    }
  }

  async getUnderwritingMessages(policyId: string): Promise<{
    success: boolean;
    data?: BusinessUnderwritingMessagesResponse;
    error?: string;
  }> {
    try {
      const path = `/portal/v1/business/quotes/${encodeURIComponent(policyId)}/underwriting-messages`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<BusinessUnderwritingMessagesResponse>(
        response
      );
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao carregar mensagens de subscrição',
      };
    }
  }

  async copyInsuredObject(
    policyId: string,
    riskPk: number
  ): Promise<{
    success: boolean;
    data?: CopyInsuredObjectResponse;
    error?: string;
  }> {
    try {
      const path = `/portal/v1/business/quotes/${policyId}/insured-object/${riskPk}/copy`;
      const response = await this.post(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<CopyInsuredObjectResponse>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao copiar local de risco',
      };
    }
  }

  async getCoverages(
    activityPerformed: string,
    versionDate: string
  ): Promise<{
    success: boolean;
    data?: Coverage[];
    error?: string;
  }> {
    try {
      const params = new URLSearchParams({
        versionDate,
        activityPerformed,
      });
      const path = `/portal/v1/products/business/coverages?${params.toString()}`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<Coverage[]>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao carregar coberturas',
      };
    }
  }

  async deleteInsuredObject(
    policyId: string,
    riskPk: number
  ): Promise<{
    success: boolean;
    data?: CopyInsuredObjectResponse;
    error?: string;
  }> {
    try {
      const path = `/portal/v1/business/quotes/${policyId}/insured-object/${riskPk}`;
      const response = await this.delete(path, PORTAL_API_URL);
      return await this.handleJsonResponse<CopyInsuredObjectResponse>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao remover local de risco',
      };
    }
  }
}

export const businessQuotesService = new BusinessQuotesService();
